import type { DiffFile, DiffHunk, DiffLine } from '@orgus/protocol';
import { DIFF_STATUS_LABEL } from '@orgus/protocol';
import { ChevronRight, Columns2, FileMinus, FilePen, FilePlus, FileSymlink, Rows3 } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { diffLineCount, highlightLine, langOf, splitRows } from '../lib/diff.ts';
import type { TokenKind } from '../lib/diff.ts';
import { formatInt, plural } from '../lib/format.ts';
import { Button } from './Button.tsx';
import { Segmented } from './Segmented.tsx';

export type DiffViewMode = 'unified' | 'split';

export interface DiffViewerProps {
  files: DiffFile[];
  /** Controlled view mode; uncontrolled starts as `defaultMode`. */
  mode?: DiffViewMode;
  defaultMode?: DiffViewMode;
  onModeChange?: (m: DiffViewMode) => void;
  /** Files whose diff has more lines than this start collapsed (default 400). */
  collapseOver?: number;
  /** Start every file collapsed. */
  collapsed?: boolean;
  /** Left of the toolbar (a scope switch, a summary). */
  toolbar?: ReactNode;
  /** Hide the toolbar (mode switch, expand and collapse all). */
  bare?: boolean;
  className?: string;
}

const STATUS_ICON: Record<DiffFile['status'], ReactNode> = {
  added: <FilePlus aria-hidden />,
  untracked: <FilePlus aria-hidden />,
  deleted: <FileMinus aria-hidden />,
  modified: <FilePen aria-hidden />,
  renamed: <FileSymlink aria-hidden />,
  copied: <FileSymlink aria-hidden />,
};

const STATUS_CLASS: Record<DiffFile['status'], string> = {
  added: 'text-passed',
  untracked: 'text-passed',
  deleted: 'text-failed',
  modified: 'text-fg-muted',
  renamed: 'text-accent-text',
  copied: 'text-accent-text',
};

const TOKEN_CLASS: Record<TokenKind, string> = {
  kw: 'text-(color:--hue-8)',
  str: 'text-(color:--hue-3)',
  num: 'text-(color:--hue-5)',
  com: 'text-fg-subtle',
  plain: '',
};

function Code({ text, lang }: { text: string; lang: string | null }) {
  const tokens = useMemo(() => highlightLine(text, lang), [text, lang]);
  return (
    <>
      {tokens.map((t, i) =>
        t.kind === 'plain' ? (
          t.text
        ) : (
          <span key={i} className={TOKEN_CLASS[t.kind]}>
            {t.text}
          </span>
        ),
      )}
    </>
  );
}

/**
 * Added and removed lines sit on a whisper of their status colour. Light uses the soft pastel
 * tokens; dark's soft tokens are deep saturated bands, so dark takes a low-alpha wash of the status
 * fill instead. The stronger colour stays on the +/- gutter.
 */
const ROW_BG: Record<DiffLine['kind'], string> = {
  add: 'bg-passed-soft in-data-[theme=dark]:bg-status-success-fill/10',
  del: 'bg-failed-soft in-data-[theme=dark]:bg-status-danger-fill/10',
  ctx: '',
  note: '',
};

const MARK: Record<DiffLine['kind'], { sign: string; sr: string; cls: string }> = {
  add: { sign: '+', sr: 'Added', cls: 'text-passed' },
  del: { sign: '-', sr: 'Removed', cls: 'text-failed' },
  ctx: { sign: ' ', sr: '', cls: 'text-fg-subtle' },
  note: { sign: '', sr: '', cls: '' },
};

const NUM = 'w-10 shrink-0 select-none pr-2 text-right text-fg-subtle num';

function LineRow({ l, lang }: { l: DiffLine; lang: string | null }) {
  if (l.kind === 'note') {
    return (
      <div role="row" className="flex text-fg-subtle italic">
        <span className={NUM} />
        <span className={NUM} />
        <span role="cell" className="px-2">
          {l.text}
        </span>
      </div>
    );
  }
  const m = MARK[l.kind];
  return (
    <div role="row" className={cn('flex min-w-max', ROW_BG[l.kind])}>
      <span role="cell" className={NUM}>
        {l.old ?? ''}
      </span>
      <span role="cell" className={NUM}>
        {l.new ?? ''}
      </span>
      <span role="cell" className={cn('w-4 shrink-0 select-none text-center', m.cls)} aria-hidden>
        {m.sign}
      </span>
      {m.sr && <span className="sr-only">{m.sr}: </span>}
      <span role="cell" className="whitespace-pre pr-4">
        <Code text={l.text} lang={lang} />
      </span>
    </div>
  );
}

function SplitCell({ l, lang, side }: { l: DiffLine | null; lang: string | null; side: 'left' | 'right' }) {
  if (!l) return <div role="cell" className="flex min-w-0 flex-1 bg-hover/40" aria-hidden />;
  if (l.kind === 'note') {
    return (
      <div role="cell" className="flex min-w-0 flex-1 italic text-fg-subtle">
        <span className={NUM} />
        <span className="px-2">{l.text}</span>
      </div>
    );
  }
  const m = MARK[l.kind];
  const no = side === 'left' ? l.old : l.new;
  return (
    <div role="cell" className={cn('flex min-w-0 flex-1 overflow-hidden', ROW_BG[l.kind])}>
      <span className={NUM}>{no ?? ''}</span>
      <span className={cn('w-4 shrink-0 select-none text-center', m.cls)} aria-hidden>
        {m.sign}
      </span>
      {m.sr && <span className="sr-only">{m.sr}: </span>}
      <span className="min-w-0 whitespace-pre-wrap break-all pr-3">
        <Code text={l.text} lang={lang} />
      </span>
    </div>
  );
}

function HunkHeader({ h }: { h: DiffHunk }) {
  return (
    <div role="row" className="flex min-w-max items-center gap-3 border-y border-border-subtle bg-surface px-3 py-0.5 text-fg-subtle">
      <span role="cell" className="num">
        {h.header}
      </span>
      {h.section && (
        <span role="cell" className="truncate">
          {h.section}
        </span>
      )}
    </div>
  );
}

function Hunks({ file, mode }: { file: DiffFile; mode: DiffViewMode }) {
  const lang = useMemo(() => langOf(file.path), [file.path]);
  return (
    <div role="table" aria-label={`Changes in ${file.path}`} className={cn('font-mono text-caption leading-5', mode === 'unified' && 'overflow-x-auto')}>
      {file.hunks.map((h, i) => (
        <div key={`${h.header}-${i}`} role="rowgroup">
          <HunkHeader h={h} />
          {mode === 'unified'
            ? h.lines.map((l, j) => <LineRow key={j} l={l} lang={lang} />)
            : splitRows(h).map((r, j) => (
                <div key={j} role="row" className="flex divide-x divide-border-subtle">
                  <SplitCell l={r.left} lang={lang} side="left" />
                  <SplitCell l={r.right} lang={lang} side="right" />
                </div>
              ))}
        </div>
      ))}
    </div>
  );
}

/** Why a file shows less than all of its changes, in words. */
function fileNote(f: DiffFile): string | null {
  if (f.binary) return 'Binary file. Its contents are not shown.';
  if (f.truncated === 'large') return 'This file is too large to show.';
  if (f.truncated === 'total') return 'Not shown: the diff reached its size limit before this file. The counts are exact.';
  if (!f.hunks.length && f.modeChange) return `Only the file mode changed (${f.modeChange}).`;
  if (!f.hunks.length && f.status === 'untracked') return 'New file, not shown (it is not a regular text file, or past the limit of files shown with content).';
  if (!f.hunks.length && (f.status === 'renamed' || f.status === 'copied')) return 'The contents did not change.';
  if (!f.hunks.length) return 'No line changes.';
  return null;
}

function ChangeBar({ add, del }: { add: number; del: number }) {
  const total = add + del;
  const boxes = 5;
  const a = total ? Math.round((add / total) * boxes) : 0;
  const d = total ? Math.min(boxes - a, Math.round((del / total) * boxes)) : 0;
  return (
    <span className="hidden gap-px sm:inline-flex" aria-hidden>
      {Array.from({ length: boxes }, (_, i) => (
        <span key={i} className={cn('size-1.5 rounded-control', i < a ? 'bg-passed' : i < a + d ? 'bg-failed' : 'bg-border')} />
      ))}
    </span>
  );
}

const FileBlock = memo(function FileBlock({
  file,
  mode,
  open,
  onToggle,
}: {
  file: DiffFile;
  mode: DiffViewMode;
  open: boolean;
  onToggle: () => void;
}) {
  const note = fileNote(file);
  const label = DIFF_STATUS_LABEL[file.status];
  return (
    <section className="rounded-card border border-border bg-raised [content-visibility:auto]" aria-label={file.path}>
      <h4 className="sticky top-[var(--diff-sticky-top,0px)] z-[1] rounded-t-card bg-raised">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={cn(
            'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm hover:bg-hover',
            open ? 'rounded-t-card border-b border-border-subtle' : 'rounded-card',
          )}
        >
          <ChevronRight className={cn('size-3.5 shrink-0 text-fg-subtle transition-transform duration-150', open && 'rotate-90')} aria-hidden />
          <span className={cn('inline-flex shrink-0 items-center gap-1 text-caption font-medium [&_svg]:size-3.5', STATUS_CLASS[file.status])}>
            {STATUS_ICON[file.status]}
            {label}
          </span>
          <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg" title={file.oldPath ? `${file.oldPath} to ${file.path}` : file.path}>
            {file.oldPath && file.oldPath !== file.path && (
              <>
                <span className="text-fg-subtle">{file.oldPath}</span>
                <span className="px-1 text-fg-subtle">to</span>
              </>
            )}
            {file.path}
          </span>
          {!file.binary && (
            <span className="flex shrink-0 items-center gap-2 text-caption num">
              <span className="text-passed">+{formatInt(file.additions)}</span>
              <span className="text-failed">-{formatInt(file.deletions)}</span>
              <ChangeBar add={file.additions} del={file.deletions} />
            </span>
          )}
        </button>
      </h4>
      {open && (
        <div className="overflow-hidden rounded-b-card">
          {file.hunks.length > 0 && <Hunks file={file} mode={mode} />}
          {note && <p className="px-3 py-2.5 text-sm text-fg-muted">{note}</p>}
          {file.truncated === 'lines' && (
            <p className="border-t border-border-subtle px-3 py-2 text-sm text-fg-muted">
              Showing the first {formatInt(diffLineCount(file.hunks))} lines of this file's changes. The counts above are exact.
            </p>
          )}
        </div>
      )}
    </section>
  );
});

/**
 * A diff: one block per file with a sticky header (status, path, +/- counts), collapsible,
 * in unified or side-by-side view, with light syntax colouring. Large files start
 * collapsed. Pure presentation: the caller fetches and passes `files`.
 */
export function DiffViewer({ files, mode: modeProp, defaultMode = 'unified', onModeChange, collapseOver = 400, collapsed, toolbar, bare, className }: DiffViewerProps) {
  const [own, setOwn] = useState<DiffViewMode>(defaultMode);
  const mode = modeProp ?? own;
  const setMode = (m: DiffViewMode) => {
    setOwn(m);
    onModeChange?.(m);
  };
  const key = (f: DiffFile, i: number) => `${i}:${f.oldPath ?? ''}:${f.path}`;
  const initial = (f: DiffFile) => !collapsed && diffLineCount(f.hunks) <= collapseOver;
  const [openState, setOpenState] = useState<Record<string, boolean>>({});
  const isOpen = (f: DiffFile, i: number) => openState[key(f, i)] ?? initial(f);
  const allOpen = files.every((f, i) => isOpen(f, i));
  const setAll = (open: boolean) => setOpenState(Object.fromEntries(files.map((f, i) => [key(f, i), open])));

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {!bare && (
        <div className="flex flex-wrap items-center gap-2">
          {toolbar}
          <div className="ml-auto flex items-center gap-2">
            {files.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => setAll(!allOpen)}>
                {allOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
            <Segmented<DiffViewMode>
              label="Diff layout"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'unified', label: 'Unified', icon: <Rows3 />, iconOnly: true },
                { value: 'split', label: 'Side by side', icon: <Columns2 />, iconOnly: true },
              ]}
            />
          </div>
        </div>
      )}
      {files.map((f, i) => (
        <FileBlock key={key(f, i)} file={f} mode={mode} open={isOpen(f, i)} onToggle={() => setOpenState((s) => ({ ...s, [key(f, i)]: !isOpen(f, i) }))} />
      ))}
    </div>
  );
}

/** "3 files, +12 -4" for a header line. */
export function diffSummary(files: readonly Pick<DiffFile, 'additions' | 'deletions'>[]): string {
  let a = 0;
  let d = 0;
  for (const f of files) {
    a += f.additions;
    d += f.deletions;
  }
  return `${plural(files.length, 'file')}, +${formatInt(a)} -${formatInt(d)}`;
}
